import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Shield, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { authenticateWithFastLoginToken } from "../services/crmServices/userService";
import { useAuth } from "../hooks/useAuth";

export function FastLogin() {
    const navigate = useNavigate();
    const { updateProfile } = useAuth();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");
    const [userInfo, setUserInfo] = useState(null);

    const token = searchParams.get("token");

    useEffect(() => {
        if (!token) {
            setError("No authentication token provided");
            setLoading(false);
            return;
        }

        authenticateUser();
    }, [token]);

    const authenticateUser = async () => {
        try {
            setLoading(true);
            const response = await authenticateWithFastLoginToken(token);
            if (response.data?.user && response.data?.token) {
                const userData = { ...response.data.user, token: response.data.token };
                updateProfile(userData);
                // Store user data in localStorage (same as normal login)
                // localStorage.setItem("crm_user", JSON.stringify(userData));

                setUserInfo(userData);
                setSuccess(true);
                toast.success("Successfully authenticated with fast login!");

                // Redirect to dashboard after a short delay
                setTimeout(() => {
                    navigate("/");
                }, 2000);
            } else if (response?.data?.message) {
                setError(response.data.message);
            } else {
                setError("Invalid or expired authentication token");
            }
        } catch (error) {
            console.error("Fast login error:", error);
            setError("Authentication failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleRetry = () => {
        if (token) {
            authenticateUser();
        }
    };

    const handleManualLogin = () => {
        navigate("/login");
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <Shield className="mx-auto h-12 w-12 text-blue-600" />
                    <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Fast Login</h2>
                    <p className="mt-2 text-sm text-gray-600">Secure token-based authentication</p>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-center">
                            {loading && "Authenticating..."}
                            {success && "Authentication Successful"}
                            {error && "Authentication Failed"}
                        </CardTitle>
                        <CardDescription className="text-center">
                            {loading && "Please wait while we verify your authentication token"}
                            {success && "You will be redirected to the dashboard shortly"}
                            {error && "There was a problem with your authentication"}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {loading && (
                            <div className="flex flex-col items-center space-y-4">
                                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                <p className="text-sm text-gray-600">Verifying authentication token...</p>
                            </div>
                        )}

                        {success && userInfo && (
                            <div className="flex flex-col items-center space-y-4">
                                <CheckCircle className="h-8 w-8 text-green-600" />
                                <div className="text-center">
                                    <p className="font-medium text-gray-900">Welcome back, {userInfo.firstName}!</p>
                                    <p className="text-sm text-gray-600">Position: {userInfo.position.name}</p>
                                    <p className="text-sm text-gray-600">Email: {userInfo.email}</p>
                                </div>
                                <div className="w-full bg-green-100 border border-green-200 rounded-lg p-3">
                                    <p className="text-sm text-green-800 text-center">
                                        Authentication successful. Redirecting to dashboard...
                                    </p>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="flex flex-col items-center space-y-4">
                                <XCircle className="h-8 w-8 text-red-600" />
                                <div className="w-full bg-red-100 border border-red-200 rounded-lg p-3">
                                    <p className="text-sm text-red-800 text-center">{error}</p>
                                </div>

                                <div className="w-full space-y-3">
                                    {token && (
                                        <Button onClick={handleRetry} className="w-full bg-blue-600 hover:bg-blue-700">
                                            Try Again
                                        </Button>
                                    )}
                                    <Button onClick={handleManualLogin} variant="outline" className="w-full">
                                        Use Manual Login
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Security Information */}
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                            <h4 className="font-medium text-blue-900 text-sm mb-1">Security Notice</h4>
                            <ul className="text-xs text-blue-800 space-y-1">
                                <li>• This token can only be used once</li>
                                <li>• Tokens expire after 24 hours</li>
                                <li>• All login activity is logged</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
